import React, { FC, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Pool, PoolPlansTab } from '../../../types/pool-plans.types';
import { PlanStakeholder } from '../../../types/stakeholder.types';
import { EmptyPools } from '../empty-pools';
import { ScrollablePoolTable, StickyPoolTable } from './pools-table/index';
import { ScrollableStakeholderTable, StickyStakeholderTable } from './stakeholder-table/index';

type PoolPlansCommonTableHeaderProps = {
  date: Date;
  isSortedByPools: boolean;
  onAddPlanClick: () => void;
  onAddPoolClick: () => void;
  onDeleteClick: (id: string) => void;
  onDeletePlanClick: (id: string) => void;
  onEditClick: (id: string) => void;
  onEditPlanClick: (id: string) => void;
  onEditPlanTerminationDateClick: (id: string) => void;
  onEditTerminationDateClick: (id: string) => void;
  onViewClick: (id: string) => void;
  onViewPlanClick: (id: string) => void;
  pools: Pool[];
  selectedTab: PoolPlansTab;
  stakeholders: PlanStakeholder[];
};

export const PoolPlansCommonTable: FC<PoolPlansCommonTableHeaderProps> = ({
  isSortedByPools,
  selectedTab,
  pools,
  stakeholders,
  onEditClick,
  onViewClick,
  onEditPlanClick,
  onViewPlanClick,
  onDeleteClick,
  onDeletePlanClick,
  onEditTerminationDateClick,
  onEditPlanTerminationDateClick,
  onAddPlanClick,
  onAddPoolClick,
  date,
}) => {
  const [hasRightShadow, setHasRightShadow] = useState(false);
  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollableRef.current) {
        const { scrollLeft } = scrollableRef.current;
        const newHasRightShadow = scrollLeft > 0;
        setHasRightShadow(newHasRightShadow);
      }
    };

    const scrollable = scrollableRef.current;
    if (scrollable) {
      scrollable.addEventListener('scroll', handleScroll);
      handleScroll();

      return () => scrollable.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const isEmpty = !(isSortedByPools ? pools.length : stakeholders.length);

  return (
    <div className="flex w-full">
      {!isEmpty ? (
        <>
          {isSortedByPools ? (
            <>
              <div
                className={twMerge(
                  'z-10 h-full flex-shrink-0',
                  hasRightShadow ? 'shadow-rightShadow' : '',
                )}
              >
                <StickyPoolTable date={date} pools={pools} selectedTab={selectedTab} />
              </div>
              <div className="h-full flex-grow overflow-x-auto" ref={scrollableRef}>
                <ScrollablePoolTable
                  date={date}
                  onDeleteClick={onDeleteClick}
                  onDeletePlanClick={onDeletePlanClick}
                  onEditClick={onEditClick}
                  onEditPlanClick={onEditPlanClick}
                  onEditPlanTerminationDateClick={onEditPlanTerminationDateClick}
                  onEditTerminationDateClick={onEditTerminationDateClick}
                  onViewClick={onViewClick}
                  onViewPlanClick={onViewPlanClick}
                  pools={pools}
                  selectedTab={selectedTab}
                />
              </div>
            </>
          ) : (
            <>
              <div
                className={twMerge(
                  'flex-shrink-0',
                  hasRightShadow ? 'z-10 shadow-rightShadow' : '',
                )}
              >
                <StickyStakeholderTable date={date} poolsByStakeholders={stakeholders} />
              </div>
              <div className="flex-grow overflow-x-auto" ref={scrollableRef}>
                <ScrollableStakeholderTable
                  date={date}
                  onDeleteClick={onDeleteClick}
                  onDeletePlanClick={onDeletePlanClick}
                  onEditClick={onEditClick}
                  onEditPlanClick={onEditPlanClick}
                  onEditTerminationDateClick={onEditTerminationDateClick}
                  onViewClick={onViewClick}
                  onViewPlanClick={onViewPlanClick}
                  poolsByStakeholders={stakeholders}
                />
              </div>
            </>
          )}
        </>
      ) : (
        <EmptyPools
          onAddPlanClick={onAddPlanClick}
          onAddPoolClick={onAddPoolClick}
          selectedTab={selectedTab}
        />
      )}
    </div>
  );
};
